import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  reduce,
  map,
  size,
  toString,
  find,
  filter,
  includes,
  uniq,
  orderBy,
  isEmpty,
} from 'lodash';
import { Box } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import Highcharts from 'highcharts';
import { format } from 'date-fns';

import { RowNode } from 'ag-grid-enterprise';

import {
  ColDef,
  GetMainMenuItemsParams,
  MenuItemDef,
  RowGroupOpenedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';

import Table from '../../../../shared/Table';

import { truncationValues } from '../../../../Visualization/visualizationConstants';
import {
  formatParameters,
  IFormatOutputParameters,
} from '../../../../Visualization/formatParameters';

import { useData } from '../../../../../hooks/useData';
import OverflowTooltip from '../../../../shared/OverflowTooltip';

import { IChartData } from '../index';

import {
  ModelInstance,
  RowIdentifier,
  Timescale,
} from '../../../../../types/models';

import { OutputsTableRef } from '../GenerateChartPanel';

interface IOutputsTableProps {
  timescale: Timescale;
  chartDateType: string;
  chartData: IChartData;
  formatKey: number;
  title: string;
  prefix: string;
  suffix: string;
  decimalPlaces: number;
  magnitudeTruncation: number;
  outputId?: RowIdentifier['id'];
  modelInstanceId?: ModelInstance['id'];
  showScenarios: boolean;
}

const OutputsTable = forwardRef<OutputsTableRef, IOutputsTableProps>(
  (
    {
      chartData,
      formatKey,
      title,
      prefix,
      suffix,
      magnitudeTruncation,
      decimalPlaces,
      outputId,
      modelInstanceId,
      chartDateType,
      timescale,
      showScenarios,
    },
    ref
  ) => {
    const [rowData, setRowData] = useState<any[]>([]);
    const { data } = useData<{
      rowIdentifiers?: RowIdentifier[];
    }>(
      () => ({
        rowIdentifiers:
          modelInstanceId !== undefined
            ? `/instances/${modelInstanceId}/row_identifiers`
            : undefined,
      }),
      []
    );

    const outputs = useMemo(
      () => filter(data.rowIdentifiers, { Type: 'Output' }),
      [modelInstanceId, data]
    );
    const gridRef = useRef<AgGridReact>(null);

    const formatType = useMemo(
      () =>
        (
          find(formatParameters, {
            formatKey,
          }) as IFormatOutputParameters
        ).numberType,
      [formatKey]
    );

    const scenarios = uniq(map(chartData.PublishedData, 'scenario'));

    const autoGroupColumnDef = useMemo<ColDef>(() => {
      return {
        headerName: 'Scenario',
        cellRendererParams: {
          suppressCount: true,
          checkbox: false,
        },
        pinned: 'left',
        cellStyle: (params: any) => {
          // Only apply gray background to top-level scenario rows (level 0)
          if (params.node.group && params.node.level === 0) {
            return {
              fontWeight: 'bold',
              lineHeight: '24px',
              paddingTop: '0px',
              paddingBottom: '0px',
              paddingLeft: '8px',
              paddingRight: '8px',
            };
          }
          return null;
        },
      };
    }, []);

    const [columns, setColumns] = useState<ColDef[]>([]);

    const exportColumns = useMemo(
      () => [
        {
          headerName: 'Segment',
          field: 'segment',
        },
        {
          headerName: 'Data Version',
          field: 'DataVersion',
        },
        ...map(chartData.Dates, (date) => {
          return {
            headerName: includes(
              [Timescale.Weekly, Timescale.Monthly],
              timescale
            )
              ? format(new Date(date.Date), chartDateType)
              : date.Date,
            field: date.Date,
            cellRenderer: (params: ValueFormatterParams) => {
              if (!(params.node as RowNode).group) {
                return `${prefix}${Highcharts.numberFormat(
                  (formatType === 'Percent'
                    ? Number(params.value) * 100
                    : Number(params.value)) /
                    truncationValues[toString(magnitudeTruncation)],
                  decimalPlaces,
                  '.'
                )
                  .toString()
                  .replaceAll(' ', '')
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${suffix}`.replace(
                  /\B(?=(\d{3})+(?!\d))/g,
                  ','
                );
              }
            },
          };
        }),
      ],
      []
    );

    // Update columns when showScenarios or chartData.PreviewData changes
    useEffect(() => {
      const baseColumns = [
        // Conditionally include Version column
        ...(!showScenarios && !isEmpty(chartData.PreviewData) ? [{
          headerName: 'Version',
          field: 'version',
          pinned: 'left' as const,
          cellRenderer: OverflowTooltip,
        }] : []),
        {
          headerName: 'Segment',
          field: 'segment',
          pinned: 'left' as const,
          cellRenderer: OverflowTooltip,
        },
        ...map(chartData.Dates, (date) => {
          return {
            headerName: includes([Timescale.Weekly, Timescale.Monthly], timescale)
              ? format(new Date(date.Date), chartDateType)
              : date.Date,
            field: date.Date,
            cellRenderer: (params: ValueFormatterParams) => {
              if (!(params.node as RowNode).group) {
                return `${prefix}${Highcharts.numberFormat(
                  (formatType === 'Percent'
                    ? Number(params.value) * 100
                    : Number(params.value)) /
                    truncationValues[toString(magnitudeTruncation)],
                  decimalPlaces,
                  '.'
                )
                  .toString()
                  .replaceAll(' ', '')
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${suffix}`.replace(
                  /\B(?=(\d{3})+(?!\d))/g,
                  ','
                );
              }
            },
          };
        }),
      ];

      // Add scenario grouping column if multiple scenarios
      const updatedColumns = scenarios.length > 1 ? [
        {
          field: 'scenario',
          rowGroup: true,
          hide: true,
          pinned: 'left' as const,
        },
        ...baseColumns,
      ] : baseColumns;

      setColumns(updatedColumns);
    }, [showScenarios, chartData.PreviewData, chartData.Dates, scenarios.length, timescale, chartDateType, prefix, suffix, formatType, magnitudeTruncation, decimalPlaces]);

    useEffect(() => {
      const publishedData = map(chartData.PublishedData, (series) => ({...series, version: 'Original'}))
      const previewData = map(chartData.PreviewData, (series) => ({...series, version: 'Recalculated'}))
      setRowData(
        map(
          [...publishedData, ...previewData],
          (series) => {
            return {
              version: series.version,
              scenario: series.scenario,
              segment: series.segment,
              ...reduce(
                chartData.Dates,
                (a: Record<string, number>, c, idx) => {
                  a[c.Date] = series.values[idx];
                  return a;
                },
                {}
              ),
            };
          }
        )
      );
    }, [chartData]);

    const exportRowData = useMemo(() => {
      return orderBy(
        [
          ...map(chartData.PreviewData, (series) => {
            return {
              DataVersion: 'Recalculated',
              id: series.id,
              scenario: series.scenario,
              segment: series.segment,
              ...reduce(
                chartData.Dates,
                (a: Record<string, number>, c, idx) => {
                  a[c.Date] = series.values[idx];
                  return a;
                },
                {}
              ),
            };
          }),
          ...map(chartData.PublishedData, (series) => {
            return {
              DataVersion: 'Original',
              id: series.id,
              scenario: series.scenario,
              segment: series.segment,
              ...reduce(
                chartData.Dates,
                (a: Record<string, number>, c, idx) => {
                  a[c.Date] = series.values[idx];
                  return a;
                },
                {}
              ),
            };
          }),
        ],
        ['segment', 'id']
      );
    }, [chartData]);

    const exportToCSV = (): void => {
      gridRef.current!.api.exportDataAsCsv({
        fileName: `${title}-${format(new Date(), 'MM/dd/yyyy_HH:mm:ss')}`,
      });
    };

    const exportToExcel = (): void => {
      gridRef.current!.api.exportDataAsExcel({
        fileName: `${title}-${format(new Date(), 'MM/dd/yyyy_HH:mm:ss')}`,
        sheetName: find(outputs, {
          id: outputId,
        })?.Name,
        author: 'Trinity LifeSciences',
      });
    };

    useImperativeHandle(ref, () => ({
      exportToCSV,
      exportToExcel,
    }));

    function getMainMenuItems(
      params: GetMainMenuItemsParams
    ): (string | MenuItemDef)[] {
      const menuItems: (MenuItemDef | string)[] = [];
      const itemsToExclude = ['separator', 'pinSubMenu', 'valueAggSubMenu'];
      params.defaultItems.forEach((item) => {
        if (itemsToExclude.indexOf(item) < 0) {
          menuItems.push(item);
        }
      });
      return menuItems;
    }

    const onRowGroupOpened = (params: RowGroupOpenedEvent) => {
      params.columnApi.autoSizeAllColumns();
    };

    const getRowStyle = (params: any): any => {
      // Only apply gray background to top-level scenario rows (level 0)
      if (params.node.group && params.node.level === 0) {
        return {
          backgroundColor: '#f5f5f5',
          fontWeight: 'bold',
        } as React.CSSProperties;
      }
      return undefined;
    };

    const getRowHeight = (params: any) => {
      // Set smaller height for scenario rows (level 0 group rows)
      if (params.node.group && params.node.level === 0) {
        return 30;
      }
      // Default height for other rows
      return 42;
    };

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'none' }}>
          <Table
            gridRef={gridRef}
            isLoading={false}
            isSuccess
            pagination={false}
            rowCount={size(rowData)}
            columnDefs={exportColumns}
            rowData={exportRowData}
          />
        </Box>
        <Table
          gridRef={gridRef}
          isLoading={false}
          isSuccess
          pagination={false}
          rowCount={size(rowData)}
          columnDefs={columns}
          rowData={rowData}
          getMainMenuItems={getMainMenuItems}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDisplayType="singleColumn"
          groupDefaultExpanded={-1}
          suppressColumnVirtualisation
          onRowGroupOpened={onRowGroupOpened}
          getRowStyle={getRowStyle}
          getRowHeight={getRowHeight}
          defaultColDef={{
            sortable: true,
          }}
          onGridReady={(params) => params.columnApi.autoSizeAllColumns()}
          onFirstDataRendered={(params) => {
            params.columnApi.autoSizeAllColumns();
            // Set default sort by segment after data is loaded
            params.columnApi.applyColumnState({
              state: [
                {
                  colId: 'segment',
                  sort: 'asc',
                },
              ],
            });
          }}
        />
      </Box>
    );
  }
);

export default OutputsTable;
